import React, { useRef } from "react";
import api from "@/lib/api";

/**
 * Renders either an HTML5 <video> for uploads OR a YouTube iframe for YouTube-sourced videos.
 * Props:
 *   videoId       - our DB id (used for /stream endpoint)
 *   youtubeId     - if present, render YouTube iframe
 *   autoPlay      - autoplay hint
 *   onFirstPlay   - called once when playback actually begins (uploads only)
 */
export default function VideoPlayer({ videoId, youtubeId, autoPlay = false, onFirstPlay, className = "" }) {
  const fired = useRef(false);
  const handlePlay = () => {
    if (fired.current) return;
    fired.current = true;
    if (onFirstPlay) onFirstPlay();
  };

  if (youtubeId) {
    // YouTube iframe embed — call onFirstPlay immediately since we can't reliably observe play events
    if (!fired.current) {
      fired.current = true;
      if (onFirstPlay) setTimeout(() => onFirstPlay(), 0);
    }
    const src = `https://www.youtube.com/embed/${encodeURIComponent(youtubeId)}?rel=0&modestbranding=1&playsinline=1${autoPlay ? "&autoplay=1" : ""}`;
    return (
      <iframe
        data-testid="youtube-embed"
        src={src}
        className={`w-full aspect-video rounded-2xl ${className}`}
        title="Video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    );
  }

  const token = api.defaults.headers.common.Authorization?.replace("Bearer ", "");
  // Include a version query to bust any stale browser cache after server-side re-encodes.
  const v = Date.now();
  const src = `${api.defaults.baseURL}/videos/${videoId}/stream?v=${v}${token ? `&auth=${encodeURIComponent(token)}` : ""}`;
  return (
    <video
      data-testid="video-player"
      className={`w-full rounded-2xl ${className}`}
      controls
      autoPlay={autoPlay}
      src={src}
      onPlay={handlePlay}
    />
  );
}
